import { Injectable } from '@angular/core';
import { core, logger } from '@icc/shared/helpers';
import { Common } from '@icc/shared/helpers';
import { IWindowActiveConfiguration, WindowActiveConfiguration } from '@icc/window-door/types';
import { Alignment, Frame } from '@icc/window-door/types';
import { ActiveSash } from '@icc/window-door/types';
import { ProfilesService } from '@icc/construction-elements/configurator';
import { EventBusService, TranslateService } from '@icc/shared';
import { InfoService } from '@icc/shared/helpers';

@Injectable()
export class AlignmentsService {

    private pendingAutoFixAndSashRemovalIds = new Set<number>();


    constructor(
        private eventBusService: EventBusService,
        private profilesService: ProfilesService,
        private infoService: InfoService,
        private translateService: TranslateService
    ) {

        this.eventBusService.subscribe(['changedFrames', 'setSystem'], data => {
            try {
                this.validateAndFixReversingProfile(
                    data.activeConfiguration as WindowActiveConfiguration
                );
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe(['validatedProfiles', 'changedBalcony', 'changedFrames'], data => {
            try {
                const conf = data?.activeConfiguration as WindowActiveConfiguration;
                if (!conf || !conf.Sashes) {
                    return;
                }

                conf.Sashes.forEach(sash => {
                    const bottomProfile = sash.frame?.bottom?.profileId
                        ? this.profilesService.getProfile(sash.frame.bottom.profileId)
                        : null;
                    const targetAlignmentProfileId = Number(bottomProfile?.sashAlignmentId) || null;
                    this.syncSashBalconyAlignment(sash, targetAlignmentProfileId, conf);
                });

                this.syncFixAndSashHardwareOnlyAlignments(conf);
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe(
            [
                'changedSashes',
                'changedFrames',
                'setSystem',
                'setFrameProfile',
                'validatedProfiles',
                'setLowThreshold',
                'unsetLowThreshold',
            ],
            data => {
            try {
                const conf = data?.activeConfiguration as WindowActiveConfiguration;
                if (!conf || !conf.Sashes) {
                    return;
                }

                this.syncFixAndSashHardwareOnlyAlignments(conf);
            } catch (err) {
                logger.error(err);
            }
        });
    }
    public areFixAndSashHardwareConflictsCovered(conf: WindowActiveConfiguration): boolean {
        const shouldAutoAdd = Number(conf?.System?.auto_add_alignment_for_fix_and_sash) === 1;
        if (!shouldAutoAdd || !conf?.Sashes) {
            return false;
        }

        const validProfileIds = new Set(
            this.getFixAndSashAlignmentProfiles(conf).map(p => p.id)
        );

        return conf.Sashes.every(sash => {
            if (sash?.type?.type !== 'F' && sash?.type?.type !== 'OFF') {
                return true;
            }

            return (['left', 'right', 'top', 'bottom'] as const).every(side => {
                const targetSide = this.getFixAndSashConflictTargetSide(sash, side, conf);
                if (!targetSide) {
                    return true;
                }

                const alignment = this.getAlignmentForFieldSide(sash, conf, targetSide);
                return !!alignment && validProfileIds.has(alignment.profileId);
            });
        });
    }

    private syncFixAndSashHardwareOnlyAlignments(conf: WindowActiveConfiguration) {
        const hasLowThreshold = conf?.Frames?.some(frame => frame?.lowThreshold);
        if (hasLowThreshold) {
            this.removeUnusedAutoFixAndSashAlignments(conf, new Set<number>(), true);
            return;
        }

        const shouldAutoAdd = Number(conf?.System?.auto_add_alignment_for_fix_and_sash) === 1;
        if (!shouldAutoAdd) {
            this.removeUnusedAutoFixAndSashAlignments(conf, new Set<number>(), true);
            return;
        }

        const preferredProfiles = this.getFixAndSashAlignmentProfiles(conf);
        if (!preferredProfiles.length) {
            this.removeUnusedAutoFixAndSashAlignments(conf, new Set<number>(), true);
            return;
        }

        const requiredAutoAlignmentIds = new Set<number>();

        conf.Sashes.forEach(sash => {
            if (sash?.type?.type !== 'F' && sash?.type?.type !== 'OFF') {
                return;
            }

            (['left', 'right', 'top', 'bottom'] as const).forEach(side => {
                const targetSide = this.getFixAndSashConflictTargetSide(sash, side, conf);
                if (!targetSide) {
                    return;
                }

                const existingAlignment = this.getAlignmentForFieldSide(sash, conf, targetSide);
                if (existingAlignment) {
                    if (this.isAutoFixAndSashAlignment(existingAlignment)) {
                        requiredAutoAlignmentIds.add(existingAlignment.id);
                    }
                    return;
                }

                this.putAlignmentInField(
                    preferredProfiles[0],
                    sash,
                    conf,
                    targetSide,
                    true
                );

                const createdAlignment = this.getAlignmentForFieldSide(sash, conf, targetSide);
                if (createdAlignment && this.isAutoFixAndSashAlignment(createdAlignment)) {
                    requiredAutoAlignmentIds.add(createdAlignment.id);
                }
                if (createdAlignment) {
                    this.infoService.openInfoModal(
                    {
                        message: this.translateService.instant('WINDOW|Dodano wyrównanie ramy'),
                        title: this.translateService.instant('WINDOW|Dodano wyrównanie ramy')
                    });
                }
            });
        });
        this.removeUnusedAutoFixAndSashAlignments(conf, requiredAutoAlignmentIds, true);
    }

    private removeUnusedAutoFixAndSashAlignments(
        conf: WindowActiveConfiguration,
        requiredAutoAlignmentIds: Set<number>,
        forceRemove = false
    ) {
        // Conflicting alignments are still required, so we clear any pending removal markers for them.
        requiredAutoAlignmentIds.forEach(id => this.pendingAutoFixAndSashRemovalIds.delete(id));

        conf.Sashes.forEach(sash => {
            (['left', 'right', 'top', 'bottom'] as const).forEach(side => {
                const alignment = this.getAlignmentForFieldSide(sash, conf, side);
                if (
                    alignment
                    && this.isAutoFixAndSashAlignment(alignment)
                    && !requiredAutoAlignmentIds.has(alignment.id)
                ) {
                    if (!forceRemove && !this.pendingAutoFixAndSashRemovalIds.has(alignment.id)) {
                        this.pendingAutoFixAndSashRemovalIds.add(alignment.id);
                        return;
                    }

                    this.removeAlignmentInField(sash, conf, side, true);
                    this.pendingAutoFixAndSashRemovalIds.delete(alignment.id);
                }
            });
        });

        this.cleanupOrphanAutoFixAndSashAlignments(conf, requiredAutoAlignmentIds);
    }

    private cleanupOrphanAutoFixAndSashAlignments(
        conf: WindowActiveConfiguration,
        requiredAutoAlignmentIds: Set<number>
    ) {
        const drawAlignmentIds = new Set<number>(
            (conf?.drawData?.alignment || [])
                .map((item: any) => Number(item?.alignmentId))
                .filter((id: number) => Number.isFinite(id))
        );

        if (!drawAlignmentIds.size || !Array.isArray(conf?.Alignments)) {
            return;
        }

        const orphanIds = conf.Alignments
            .filter((alignment) =>
                this.isAutoFixAndSashAlignment(alignment)
                && !requiredAutoAlignmentIds.has(alignment.id)
                && !drawAlignmentIds.has(Number(alignment.id))
            )
            .map((alignment) => Number(alignment.id));

        if (!orphanIds.length) {
            return;
        }

        const orphanIdsSet = new Set<number>(orphanIds);
        const sides: Array<keyof ActiveSash['nearAlignments']> = ['left', 'right', 'top', 'bottom'];
        const existingMullionIds = new Set<number>();

        const collectSashMullionIds = (sash: ActiveSash) => {
            ((sash as any).intMullions || []).forEach((mullion: any) => {
                if (Number.isFinite(Number(mullion?.id))) {
                    existingMullionIds.add(Number(mullion.id));
                }
            });

            (sash.intSashes || []).forEach((intSash: ActiveSash) => collectSashMullionIds(intSash));
        };

        ((conf as any).Mullions || []).forEach((mullion: any) => {
            if (Number.isFinite(Number(mullion?.id))) {
                existingMullionIds.add(Number(mullion.id));
            }
        });
        (conf.Sashes || []).forEach((sash: ActiveSash) => collectSashMullionIds(sash));

        const clearSashAlignmentReferences = (sash: ActiveSash) => {
            sides.forEach((side) => {
                if (orphanIdsSet.has(Number(sash?.nearAlignments?.[side]))) {
                    sash.nearAlignments[side] = -1;
                }
            });

            (sash.intSashes || []).forEach((intSash: ActiveSash) => clearSashAlignmentReferences(intSash));
        };

        (conf.Sashes || []).forEach((sash: ActiveSash) => clearSashAlignmentReferences(sash));

        const clearMullionAlignmentReferences = (mullions: any[] | undefined) => {
            if (!Array.isArray(mullions)) {
                return;
            }

            mullions.forEach((mullion: any) => {
                sides.forEach((side) => {
                    if (Array.isArray(mullion?.parallelAlignments?.[side])) {
                        mullion.parallelAlignments[side] = mullion.parallelAlignments[side]
                            .filter((id: number) => !orphanIdsSet.has(Number(id)));
                    }
                    if (Array.isArray(mullion?.perpendicularAlignments?.[side])) {
                        mullion.perpendicularAlignments[side] = mullion.perpendicularAlignments[side]
                            .filter((id: number) => !orphanIdsSet.has(Number(id)));
                    }
                });
            });
        };

        const clearSashMullionAlignmentReferences = (sash: ActiveSash) => {
            clearMullionAlignmentReferences((sash as any).intMullions);
            (sash.intSashes || []).forEach((intSash: ActiveSash) =>
                clearSashMullionAlignmentReferences(intSash)
            );
        };

        clearMullionAlignmentReferences((conf as any).Mullions);
        (conf.Sashes || []).forEach((sash: ActiveSash) => clearSashMullionAlignmentReferences(sash));

        conf.Alignments.forEach((alignment) => {
            alignment.perpendicularMullions ??= {
                left: [],
                right: [],
                top: [],
                bottom: [],
            };
            alignment.parallelMullions ??= {
                left: [],
                right: [],
                top: [],
                bottom: [],
            };
            alignment.parallelAlignments ??= {
                left: [],
                right: [],
                top: [],
                bottom: [],
            };
            alignment.perpendicularAlignments ??= {
                left: [],
                right: [],
                top: [],
                bottom: [],
            };

            sides.forEach((side) => {
                alignment.perpendicularMullions[side] = (alignment.perpendicularMullions?.[side] || [])
                    .filter((id) => existingMullionIds.has(Number(id)));
                alignment.parallelMullions[side] = (alignment.parallelMullions?.[side] || [])
                    .filter((id) => existingMullionIds.has(Number(id)));
                alignment.parallelAlignments[side] = (alignment.parallelAlignments?.[side] || [])
                    .filter((id) => !orphanIdsSet.has(Number(id)));
                alignment.perpendicularAlignments[side] = (alignment.perpendicularAlignments?.[side] || [])
                    .filter((id) => !orphanIdsSet.has(Number(id)));
            });
        });

        conf.Alignments = conf.Alignments.filter(
            (alignment) => !orphanIdsSet.has(Number(alignment.id))
        );
        orphanIds.forEach((id) => this.pendingAutoFixAndSashRemovalIds.delete(id));
        conf.Layout.changed = true;
    }

    private getFixAndSashAlignmentProfiles(conf: WindowActiveConfiguration) {
        const options = {
            and: ['alignment_frame'],
            not: ['alignment_threshold', 'alignment_sash'],
        };

        return this.profilesService.getFilteredProfiles(conf, null, options);
    }

    private getFixAndSashConflictTargetSide(
        sash: ActiveSash,
        side: keyof ActiveSash['nearAlignments'],
        conf: WindowActiveConfiguration
    ): keyof ActiveSash['nearAlignments'] | null {
        const mullionId = sash?.nearMullions?.[side];
        if (!Common.isNumber(mullionId) || mullionId < 0) {
            return null;
        }

        const mullion = core.fIdO<any>(conf.Mullions, mullionId);
        if (!mullion) {
            return null;
        }

        const frame = conf.Frames?.find(f => Number(f.id) === Number(sash.frameId));
        if (!frame) {
            return null;
        }

        const sideOrder = ['left', 'top', 'right', 'bottom'] as const;
        const getProfileFlags = (profile: any) => {
            const options: string[] = Array.isArray(profile?.options) ? profile.options : [];
            const hasGlazingOption = options.includes('for_glazing');
            const hasHardwareOption = options.includes('for_hardware');
            const hasBothOption = options.includes('for_glazing_or_hardware');
            const hasGlazingField = profile?.for_glazing === 1 || profile?.for_glazing === true;
            const hasHardwareField = profile?.for_hardware === 1 || profile?.for_hardware === true;
            const hasGlazing = hasGlazingOption || hasBothOption || hasGlazingField;
            const hasHardware = hasHardwareOption || hasBothOption || hasHardwareField;

            return {
                options,
                hasGlazing,
                hasHardware,
                isHardwareOnly: hasHardware && !hasGlazing,
                isGlazingOnly: hasGlazing && !hasHardware,
            };
        };

        const frameProfilesBySide = sideOrder
            .map((sideName, index) => {
                const sideFrameProfile = frame.frame?.find(p => p.side === sideName) || frame.frame?.[index];
                const profileId = sideFrameProfile?.alt?.profileId || sideFrameProfile?.profileId;
                const profile = profileId ? this.profilesService.getProfile(profileId) : null;
                const flags = getProfileFlags(profile);

                return {
                    side: sideName,
                    profileId,
                    profile,
                    ...flags,
                };
            })
            .filter(item => !!item.profile);

        const currentSideProfile = frameProfilesBySide.find(item => item.side === side);

        if (!currentSideProfile?.profile) {
            return null;
        }

        const hasAnyHardwareOnlyProfile = frameProfilesBySide.some(item => item.isHardwareOnly);

        if (!hasAnyHardwareOnlyProfile) {
            return null;
        }

        const oppositeSashesField = {
            left: 'multiAlignLeft',
            right: 'multiAlignRight',
            top: 'multiAlignTop',
            bottom: 'multiAlignBottom',
        }[side];

        const oppositeSashes: ActiveSash[] = mullion?.[oppositeSashesField] || [];
        const hasOpeningSashOnOppositeSide = oppositeSashes.some(
            adjacentSash => !this.isFixSashType(adjacentSash?.type?.type)
        );

        if (!hasOpeningSashOnOppositeSide) {
            return null;
        }

        // Prefer placing alignment on the same side when it is pure hardware-only,
        // otherwise pick the first hardware-only frame side.
        const targetSide = currentSideProfile.isHardwareOnly
            ? side
            : (frameProfilesBySide.find(item => item.isHardwareOnly)?.side ?? null);

        if (!targetSide) {
            return null;
        }

        // Wyrównanie dodajemy tylko gdy szklenie stałego skrzydła sąsiaduje bezpośrednio
        // z profilem ramy do okuwania — brak słupka po stronie docelowej.
        if (!Common.isNumber(sash.nearMullions[targetSide]) || sash.nearMullions[targetSide] >= 0) {
            return null;
        }

        return targetSide;
    }

    private isFixSashType(sashType?: string): boolean {
        return ['F', 'FF', 'OFF'].includes(sashType || '');
    }

    private isAutoFixAndSashAlignment(alignment: Alignment | null): boolean {
        return (
            !!alignment
            && alignment.autoCreatedBy === true
        );
    }

    private syncSashBalconyAlignment(
        sash: ActiveSash,
        targetAlignmentProfileId: number | null,
        conf: WindowActiveConfiguration
    ) {
        const bottomFields = (sash.intSashes || []).filter(
            (field: ActiveSash) => field.nearMullions.bottom === -1
        );

        bottomFields.forEach((field: ActiveSash) => {
            const currentAlignment = this.getAlignmentForFieldSide(field, conf, 'bottom');
            const shouldRemove =
                !!currentAlignment
                && this.isSashAlignmentProfile(currentAlignment.profileId)
                && (!conf.Balcony
                    || !targetAlignmentProfileId
                    || currentAlignment.profileId !== targetAlignmentProfileId);

            if (shouldRemove) {
                this.removeAlignmentInField(field, conf, 'bottom', true);
            }

            if (!conf.Balcony || !targetAlignmentProfileId) {
                return;
            }

            const alignmentProfile = this.profilesService.getProfile(targetAlignmentProfileId);
            if (!alignmentProfile || !this.isSashAlignmentProfile(alignmentProfile.id)) {
                return;
            }

            if (field.nearAlignments.bottom > -1) {
                this.removeAlignmentInField(field, conf, 'bottom', true);
            }

            this.putAlignmentInField(alignmentProfile, field, conf, 'bottom', true);

            const createdAlignment = this.getAlignmentForFieldSide(field, conf, 'bottom');
            if (createdAlignment) {
                this.infoService.openInfoModal({
                    message: this.translateService.instant('WINDOW|Dodano wyrównanie skrzydła'),
                    title: this.translateService.instant('WINDOW|Dodano wyrównanie skrzydła')
                });
            }
        });
    }

    private getAlignmentForFieldSide(
        field: ActiveSash,
        conf: WindowActiveConfiguration,
        side: keyof ActiveSash['nearAlignments']
    ): Alignment | null {
        const alignments = this.getAlignmentsForField(field, conf);
        const alignmentId = field?.nearAlignments?.[side];
        if (!alignments || alignmentId === undefined || alignmentId === null || alignmentId < 0) {
            return null;
        }

        return core.fIdO<Alignment>(alignments, alignmentId) || null;
    }

    private getAlignmentsForField(field: ActiveSash, conf: WindowActiveConfiguration) {
        if (Common.isDefined(field.parentId) && field.parentId !== null) {
            const sash = core.fIdO<any>(conf.Sashes, field.parentId);

            return sash?.intAlignments || [];
        }

        return conf.Alignments || [];
    }

    private isSashAlignmentProfile(profileId?: number | null): boolean {
        if (!profileId) {
            return false;
        }

        const profile = this.profilesService.getProfile(profileId);

        return !!profile && Array.isArray(profile.options) && profile.options.includes('alignment_sash');
    }

    putAlignmentInField(profile, field, conf: WindowActiveConfiguration, side, autoCreatedBy?: boolean) {
        const sides = {
            left: {
                direction: 'vertical',
                position: ['rx'],
                shift: 'ry',
                length: 'rHeight',
                adjSashes: 'right',
                perp: ['top', 'bottom'],
            },
            right: {
                direction: 'vertical',
                position: ['rx', 'rWidth'],
                shift: 'ry',
                length: 'rHeight',
                adjSashes: 'left',
                perp: ['top', 'bottom'],
            },
            top: {
                direction: 'horizontal',
                position: ['ry'],
                shift: 'rx',
                length: 'rWidth',
                adjSashes: 'bottom',
                perp: ['left', 'right'],
            },
            bottom: {
                direction: 'horizontal',
                position: ['ry', 'rHeight'],
                shift: 'rx',
                length: 'rWidth',
                adjSashes: 'top',
                perp: ['left', 'right'],
            },
        };
        let alignments;
        if (Common.isDefined(field.parentId) && field.parentId !== null) {
            const sash = core.fIdO(conf.Sashes, field.parentId);
            alignments = sash.intAlignments;
        } else {
            alignments = conf.Alignments;
        }
        const direction = sides[side].direction;
        const position = sides[side].position.reduce((prev, key) => prev + field[key], 0);
        const shift = field[sides[side].shift];
        const length = field[sides[side].length];
        const newAlignmentId = this.getIdForNew(conf);
        const newAlignment: Alignment = {
            id: newAlignmentId,
            profileId: profile.id,
            frameId: field.frameId,
            position,
            shift,
            length,
            direction,
            side,
            autoCreatedBy,
            adjacentSashes: {
                top: [],
                bottom: [],
                left: [],
                right: [],
            },
            perpendicularMullions: {
                top: [],
                bottom: [],
                left: [],
                right: [],
            },
            parallelMullions: {
                top: [],
                bottom: [],
                left: [],
                right: [],
            },
            perpendicularAlignments: {
                top: [],
                bottom: [],
                left: [],
                right: [],
            },
            parallelAlignments: {
                top: [],
                bottom: [],
                left: [],
                right: [],
            },
        };
        newAlignment.adjacentSashes[sides[side].adjSashes] = [field.id];
        newAlignment.perpendicularMullions[sides[side].perp[0]] = this.wrapIdByArray(
            field.nearMullions[sides[side].perp[0]]
        );
        newAlignment.perpendicularMullions[sides[side].perp[1]] = this.wrapIdByArray(
            field.nearMullions[sides[side].perp[1]]
        );
        newAlignment.parallelMullions[side] = this.wrapIdByArray(field.nearMullions[side]);
        newAlignment.perpendicularAlignments[sides[side].perp[0]] = this.wrapIdByArray(
            field.nearAlignments[sides[side].perp[0]]
        );
        newAlignment.perpendicularAlignments[sides[side].perp[1]] = this.wrapIdByArray(
            field.nearAlignments[sides[side].perp[1]]
        );
        newAlignment.parallelAlignments[side] = this.wrapIdByArray(field.nearAlignments[side]);

        if (field.nearAlignments[side] > -1) {
            const nearAlignment = core.fIdO<any>(alignments, field.nearAlignments[side]);
            nearAlignment.adjacentSashes[sides[side].adjSashes] = nearAlignment.adjacentSashes[
                sides[side].adjSashes
            ].filter(el => el !== field.id);
            nearAlignment.parallelAlignments[sides[side].adjSashes].push(newAlignmentId);
        }

        field.nearMullions[side] = -1;
        field.nearAlignments[side] = newAlignmentId;

        alignments.push(newAlignment);
        conf.Layout.changed = true;
        if (newAlignment && !autoCreatedBy) {
            const isIntAlignment = Common.isDefined(field.parentId) && field.parentId !== null;
            if(isIntAlignment)
            {
                this.infoService.openInfoModal({
                    message: this.translateService.instant('WINDOW|Dodano wyrównanie skrzydła'),
                    title: this.translateService.instant('WINDOW|Dodano wyrównanie skrzydła')
                });
            }
            else{
                this.infoService.openInfoModal({
                    message: this.translateService.instant('WINDOW|Dodano wyrównanie ramy'),
                    title: this.translateService.instant('WINDOW|Dodano wyrównanie ramy')
                });
            }
        }
        this.eventBusService.post({
            key: 'putAlignmentInField',
            value: {
                profile,
                field,
                side,
            },
            conf
        });
    }

    removeAlignmentInField(field, conf: WindowActiveConfiguration, side, allowAutoRemove = false) {
        const sides = {
            left: {
                direction: 'vertical',
                position: ['rx'],
                shift: 'ry',
                length: 'rHeight',
                adjSashes: 'right',
                perp: ['top', 'bottom'],
            },
            right: {
                direction: 'vertical',
                position: ['rx', 'rWidth'],
                shift: 'ry',
                length: 'rHeight',
                adjSashes: 'left',
                perp: ['top', 'bottom'],
            },
            top: {
                direction: 'horizontal',
                position: ['ry'],
                shift: 'rx',
                length: 'rWidth',
                adjSashes: 'bottom',
                perp: ['left', 'right'],
            },
            bottom: {
                direction: 'horizontal',
                position: ['ry', 'rHeight'],
                shift: 'rx',
                length: 'rWidth',
                adjSashes: 'top',
                perp: ['left', 'right'],
            },
        };
        let alignments;
        let mullions;
        if (Common.isDefined(field.parentId) && field.parentId !== null) {
            const sash = core.fIdO(conf.Sashes, field.parentId);
            alignments = sash.intAlignments;
            mullions = sash.intMullions;
        } else {
            alignments = conf.Alignments;
            mullions = conf.Mullions;
        }
        const reverseSide = sides[side].adjSashes;
        const alignmentToRemove = alignments.filter(
            alignment => field.nearAlignments[side] === alignment.id
        )[0];

        if (!alignmentToRemove) {
            return;
        }

        if (
            this.isAutoFixAndSashAlignment(alignmentToRemove)
            && !allowAutoRemove
        ) {
            return;
        }

        if (alignmentToRemove.parallelAlignments[side].length > 0) {
            const nearAlignment = core.fIdO<Alignment>(
                alignments,
                alignmentToRemove.parallelAlignments[side][0]
            );
            field.nearAlignments[side] = nearAlignment.id;
            nearAlignment.adjacentSashes[reverseSide].push(field.id);
            nearAlignment.parallelAlignments[reverseSide] = nearAlignment.parallelAlignments[
                reverseSide
            ]
                .filter(el => el !== alignmentToRemove.id)
                .concat(alignmentToRemove.parallelAlignments[reverseSide]);
        } else {
            field.nearAlignments[side] = -1;
        }

        if (alignmentToRemove.parallelMullions[side].length > 0) {
            const nearMullion = core.fIdO<Alignment>(
                mullions,
                alignmentToRemove.parallelMullions[side][0]
            );
            field.nearMullions[side] = nearMullion.id;
            // nearMullion[`multiAlign${core.capitalize(reverseSide)}`].push(field);
        } else {
            field.nearMullions[side] = -1;
        }

        if (alignmentToRemove.parallelAlignments[reverseSide].length > 0) {
            alignmentToRemove.parallelAlignments[reverseSide]
                .map(nearAlignmentId => core.fIdO<Alignment>(alignments, nearAlignmentId))
                .forEach(nearAlignment => {
                    nearAlignment.parallelAlignments[side] = nearAlignment.parallelAlignments[side]
                        .filter(el => el !== alignmentToRemove.id)
                        .concat(alignmentToRemove.parallelAlignments[side]);
                });
        }

        const oldAlignmentIndex = alignments.findIndex(
            alignment => alignment.id === alignmentToRemove.id
        );

        alignments.splice(oldAlignmentIndex, 1);
        conf.Layout.changed = true;
        this.eventBusService.post({
            key: 'removedAlignmentInField',
            value: {
                field,
                side,
            },
        });
    }

    expandAlignments(conf, side, perpendicularSide, perpendicularSideRev, field, toSash) {
        let alignments;
        if (Common.isDefined(field.parentId) && field.parentId !== null) {
            const sash = core.fIdO<any>(conf.Sashes, field.parentId);
            alignments = sash.intAlignments;
        } else {
            alignments = conf.Alignments;
        }
        if (field.nearAlignments[side] !== -1) {
            let alignment = core.fIdO<Alignment>(alignments, field.nearAlignments[side]);
            do {
                if (side === 'left' || side === 'right') {
                    alignment.length = parseInt(alignment.length + toSash.rHeight);
                } else {
                    alignment.length = parseInt(alignment.length + toSash.rWidth);
                }
                alignment.perpendicularMullions[
                    perpendicularSide
                ] = alignment.perpendicularMullions[perpendicularSide].filter(
                    mullionId => mullionId !== toSash.nearMullions[perpendicularSideRev]
                );
                alignment = core.fIdO<Alignment>(alignments, alignment.parallelAlignments[side][0]);
            } while (
                alignment
                && alignment.parallelAlignments[side].length > -1
                && alignment.perpendicularMullions[perpendicularSide].length > 0
            );
        }
    }

    rearrangeAlignments(conf: IWindowActiveConfiguration, side: keyof ActiveSash['nearAlignments'], field: ActiveSash, nearMullionId?: number) {
        let alignments;
        if (Common.isDefined(field.parentId) && field.parentId !== null) {
            const sash = core.fIdO(conf.Sashes, field.parentId);
            alignments = sash.intAlignments;
        } else {
            alignments = conf.Alignments;
        }
        if (field.nearAlignments[side] !== -1) {
            let alignment = core.fIdO<Alignment>(alignments, field.nearAlignments[side]);
            do {
                switch (side) {
                    case 'left':
                        alignment.position = field.rx;
                        alignment.shift = field.ry;
                        alignment.length = field.rHeight;
                        break;
                    case 'right':
                        alignment.position = field.rx + field.rWidth;
                        alignment.shift = field.ry;
                        alignment.length = field.rHeight;
                        break;
                    case 'top':
                        alignment.position = field.ry;
                        alignment.shift = field.rx;
                        alignment.length = field.rWidth;
                        break;
                    case 'bottom':
                        alignment.position = field.ry + field.rHeight;
                        alignment.shift = field.rx;
                        alignment.length = field.rWidth;
                        break;
                }
                if (nearMullionId) {
                    alignment.parallelMullions[side] = alignment.parallelMullions[side].filter(
                        mullionId => mullionId !== nearMullionId
                    );
                }
                alignment = core.fIdO<Alignment>(alignments, alignment.parallelAlignments[side][0]);
            } while (alignment && alignment.parallelAlignments[side].length > -1);
        }
    }

    resizeAlignments(conf: WindowActiveConfiguration, side, perpendicularSide, field: ActiveSash, length: number, lengthChange = false) {
        let alignments;
        if (Common.isDefined(field.parentId) && field.parentId !== null) {
            const sash = core.fIdO<any>(conf.Sashes, field.parentId);
            alignments = sash.intAlignments;
        } else {
            alignments = conf.Alignments;
        }
        if (field.nearAlignments[side] !== -1) {
            let alignment = core.fIdO<Alignment>(alignments, field.nearAlignments[side]);
            do {
                if (field.nearMullions[perpendicularSide] === -1 || lengthChange) {
                    alignment.length = Math.round(alignment.length + length);
                }
                if (
                    (perpendicularSide === 'left' || perpendicularSide === 'top')
                    && alignment.shift !== 0
                ) {
                    alignment.shift = lengthChange
                        ? Math.round(alignment.shift - length)
                        : Math.round(alignment.shift + length);
                }
                alignment = core.fIdO<Alignment>(alignments, alignment.parallelAlignments[side][0]);
            } while (
                alignment
                && alignment.parallelAlignments[side].length > -1
                && alignment.perpendicularMullions[perpendicularSide].length > 0
            );
        }
    }

    validateAndFixReversingProfile(conf: WindowActiveConfiguration) {
        if(conf.type !== 'door') {
            return true;
        }
        const systemSplitSashes = conf.System.split_fix_and_sash || conf.System.split_fix_and_outward_sash;
        const frameWithLightAndActiveDoorSashOuter = conf.Frames.filter(frame => this.frameHasLightsWithActiveDoorSashOuter(frame, conf));
        const reverseProfiles = this.profilesService.getProfilesByType('reversing').filter(profile => profile.systems.includes(Number(conf.System.id)));
        const usedReversingProfiles = conf.UsedProfiles.filter(profile => profile.type === 'reversing');
        const reverseProfileValid = usedReversingProfiles.some(profile => reverseProfiles.length > 0 && reverseProfiles[0].id === profile.id);
        if(systemSplitSashes || !frameWithLightAndActiveDoorSashOuter.length || !reverseProfiles.length || !reverseProfileValid) {
            usedReversingProfiles.forEach(profile => {
                const reversingAlignment = conf.Alignments.filter(alignment => alignment.profileId === profile.id);
                reversingAlignment.forEach(alignment => {
                    const sashes = conf.Sashes.filter(sash => sash.nearAlignments[alignment.side] === alignment.id);
                    sashes.forEach(sash => {
                       this.removeAlignmentInField(sash, conf, alignment.side, true)
                    });
                });
            });
        }
        if(!systemSplitSashes && frameWithLightAndActiveDoorSashOuter.length && reverseProfiles.length && !reverseProfileValid) {
            frameWithLightAndActiveDoorSashOuter.forEach(frame => {
                const activeSashesOuter = conf.Sashes.filter(sash => sash.type && sash.type.type === 'DOA' && Number(sash.frameId) === Number(frame.id));
                let sides = ['left', 'right', 'top'];
                const jointAngles = reverseProfiles[0].jointAngles || conf.System.frame_joint_angles;
                switch(jointAngles) {
                    case 'R_L_R_L':
                        sides = ['top', 'left', 'right'];
                        break;
                    case 'R_R_R_R':
                        sides = ['left', 'top', 'right'];
                        break;
                    case 'L_L_L_L':
                        sides = ['right', 'top', 'left'];
                        break;
                }
                activeSashesOuter.forEach(sash => {
                    sides.forEach(side => {
                        this.putAlignmentInField({...reverseProfiles[0], widthOut: 0, jointAngles}, sash, conf, side);
                    });
                });
            });
        }
        return true;
    }

    frameHasLightsWithActiveDoorSashOuter(frame: Frame, conf: WindowActiveConfiguration) {
        const sashesInFrame = conf.Sashes.filter(sash => Number(sash.frameId) === Number(frame.id));
        const hasActiveSashOuter = sashesInFrame.some(sash => sash.type && sash.type.type === 'DOA');
        const hasFSash = sashesInFrame.some(sash => sash.type && sash.type.type === 'F');
        return hasActiveSashOuter && hasFSash;
    }

    isAvailableAlignments(field, conf: WindowActiveConfiguration) {
        const options = {
            and: [Common.isNumber(field.parentId) ? 'alignment_sash' : 'alignment_frame'],
            not: [],
        };
        return this.profilesService.getFilteredProfiles(conf, null, options).length > 0;
    }

    getThresholdAlignments(conf: WindowActiveConfiguration) {
        const options = {
            and: ['alignment_threshold'],
            not: [],
        };
        return this.profilesService.getFilteredProfiles(conf, null, options);
    }

    private getIdForNew(conf: WindowActiveConfiguration) {
        const intAlignmentsId = conf.Sashes.reduce((prev, sash) => {
            const maxId = sash.intAlignments.reduce(
                (intPrev, intAlignment) => (intAlignment.id > intPrev ? intAlignment.id : intPrev),
                0
            );
            return maxId > prev ? maxId : prev;
        }, -1);
        const alignmentsId = conf.Alignments.reduce((prev, el) => {
            return el.id > prev ? el.id : prev;
        }, -1);
        return Math.max(alignmentsId, intAlignmentsId) + 1;
    }

    private wrapIdByArray(id) {
        return id > -1 ? [id] : [];
    }
}
